import { createSignal, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { api, createApiQuery } from "@/lib/api";
import { cn, sharedApi } from "@stockpilot/shared";
import { msg } from "@lingui/core/macro";
import { AlertTriangle, Loader2, Trash2, Undo2 } from "@fleet/icons";

/**
 * Profile danger zone: entry point for account deletion.
 *
 * - The "Delete account" button NO LONGER opens an inline dialog: it NAVIGATES to the
 *   dedicated route (`/profile/delete-account`, per-tab `/u/$u/...`). That page explains
 *   what is lost and requires re-typing the email to confirm (anti-accidental-deletion
 *   / confusion with "sign out"). See `profile.delete-account.tsx`.
 * - The PENDING deletion state (banner + cancel/restore) stays here: this is
 *   where users land when signing in again during the grace period. The dedicated
 *   page reflects it as well.
 */
export function DangerZoneSection() {
  const { t } = useI18n();
  const [busy, setBusy] = createSignal(false);

  const statusQuery = createApiQuery(
    () => ["account-deletion-status"],
    async () => sharedApi.getDeletionStatus(api),
    () => ({ suspense: false }),
  );

  const purgeAt = () => statusQuery.data?.purgeAt ?? null;
  const pending = () => Boolean(purgeAt());
  const purgeDate = () => {
    const at = purgeAt();
    return at ? new Date(at).toLocaleDateString() : "";
  };

  const cancelDeletion = () => {
    void (async () => {
      setBusy(true);
      try {
        await sharedApi.restoreAccount(api);
        await statusQuery.refetch();
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <Card class="border-destructive/30">
      <CardHeader class="pb-2">
        <div class="flex items-center gap-2">
          <AlertTriangle class="h-5 w-5 text-destructive" />
          <CardTitle>{t(msg`Delete account`)}</CardTitle>
        </div>
      </CardHeader>
      <CardContent class="space-y-4">
        <Show
          when={pending()}
          fallback={
            <>
              <p class="text-sm text-muted-foreground">
                {t(
                  msg`Your account and all its data will be permanently deleted after a 30-day grace period. You can recover it by signing in and cancelling the deletion before that date.`,
                )}
              </p>
              <Link
                to="/profile/delete-account"
                class={cn(
                  buttonVariants({ variant: "destructive" }),
                  "w-full rounded-2xl gap-2 h-12",
                )}
                data-testid="delete-account-button"
              >
                <Trash2 class="h-5 w-5" />
                {t(msg`Delete account`)}
              </Link>
            </>
          }
        >
          <div
            class="flex items-start gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-3"
            data-testid="deletion-pending-banner"
          >
            <AlertTriangle class="mt-0.5 h-4 w-4 text-destructive" />
            <div class="space-y-1">
              <p class="text-sm font-bold text-destructive">{t(msg`Account deletion scheduled`)}</p>
              <p class="text-xs text-muted-foreground">
                {(() => {
                  const date = purgeDate();
                  return t(msg`Your account will be permanently deleted on ${date}.`);
                })()}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            class="w-full rounded-2xl gap-2 h-12"
            onClick={cancelDeletion}
            disabled={busy()}
            data-testid="cancel-deletion-button"
          >
            <Show when={!busy()} fallback={<Loader2 class="h-5 w-5 animate-spin" />}>
              <Undo2 class="h-5 w-5" />
            </Show>
            {t(msg`Cancel deletion and keep my account`)}
          </Button>
        </Show>
      </CardContent>
    </Card>
  );
}
